import React from "react";
import { get } from "lodash";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { AgGridReact } from "ag-grid-react";
import { Header, Button, Message } from "semantic-ui-react";
import { isEqual, isEmpty, omit, pick } from "lodash";
import { DatesRangeInput } from "semantic-ui-calendar-react";

import {
    loadProductGridPage,
    changeProductGridPage,
    changeProductGridState,
    fetchAllProducts,
} from "./actions";
import { showProductEditorModal, loadProductForEdit } from "../editor/actions";
import CustomerContainerPicker from "../../customer/containers/picker";
import GridPageSizePicker from "../../common/components/grid-page-size-picker";

import { parseAmount } from "../../common/helpers";
import { DateFormat } from "utilities/dist/invoice/constants";
import {
    InternalSkuFeature,
    InventoryFeatureDetails,
    isFeatureActive,
} from "../../company/feature/helpers";
import { allGridColumns } from "./columns";
import { ProductTypeAcronyms, ProductTypes } from "../constants";

const NoProductsOverlay = () => {
    return (
        <div className="tm-20">
            <Header>You have not created any product yet.</Header>
        </div>
    );
};

const LoadingProductsOverlay = () => {
    return (
        <div className="tm-20">
            <Header>Loading your product catalog, hang tight...</Header>
        </div>
    );
};

class DataGrid extends React.Component {
    state = {
        isExporting: false,
        exportError: null,
    };
    grid = null;

    gridDataSource = {
        rowCount: undefined,
        getRows: params => {
            const filterModel = { ...params.filterModel };

            const acronym = filterModel.product_type?.filter.toUpperCase();
            if (filterModel.product_type?.filter) {
                const product_type = Object.keys(ProductTypeAcronyms).find(
                    key => ProductTypeAcronyms[key] === acronym
                );
                const dbValue = ProductTypes[product_type];
                if (dbValue !== undefined) {
                    filterModel.product_type.filter = dbValue;
                }
            }

            const result = this.props.loadProductGridPage(this.props, { ...params, filterModel });
            if (filterModel.product_type?.filter) {
                filterModel.product_type.filter = acronym;
            }
            return result;
        },
    };

    componentWillUnmount() {
        this.grid = null;
    }

    onGridReady = grid => {
        this.grid = grid;

        this.isInternalSkuActive(this.props.company);
        // Ensure that if there are products already available, loading state is not shown
        if (!this.props.products?.length) {
            this.grid.api.showLoadingOverlay();
        }

        this.grid.columnApi.applyColumnState({ state: this.props.sortModel, defaultState: {} });
        this.grid.api.setFilterModel(this.props.filterModel);
        this.grid.api.setDatasource(this.gridDataSource);

        this.grid.api.addEventListener("sortChanged", () => {
            const colStates = this.grid.columnApi.getColumnState();
            const sortModel = colStates
                .filter(colState => colState.sort)
                .map(colState => ({
                    colId: colState.colId,
                    sort: colState.sort,
                }));

            this.props.changeProductGridState({
                sortModel,
            });
        });

        this.grid.api.addEventListener("filterChanged", () => {
            this.props.changeProductGridState({
                filterModel: this.grid.api.getFilterModel(),
            });
        });

        this.grid.api.addEventListener("rowDoubleClicked", e => {
            if (!e.data || !e.data.id) return;

            this.props.loadProductForEdit({
                ...e.data,
                product_price: parseAmount(e.data.product_price),
            });
            this.props.showProductEditorModal(true);
        });

        this.grid.api.addEventListener("rowClicked", e => {
            if (!e.data || !e.data.id || !this.props.onRowSelect) return;

            this.props.onRowSelect(e.data, this.props.customerId);
        });
    };

    changePage = () => {
        if (this.grid) {
            const page = this.grid.api.paginationGetCurrentPage() + 1;

            if (page === this.props.currentPage) return;

            this.props.changeProductGridPage(page);
        }
    };

    componentDidUpdate(prevProps) {
        if (!this.grid) return;
        this.isInternalSkuActive(this.props.company);

        if (prevProps.isLoading && !this.props.isLoading) {
            this.grid.api.hideOverlay();
            this.grid.api.setFilterModel(this.props.filterModel);
        }

        if (!prevProps.isLoading && this.props.isLoading && !this.props.products?.length) {
            this.grid.api.showLoadingOverlay();
        }

        if (!this.props.isLoading && !this.props.products?.length) {
            this.grid.api.showNoRowsOverlay();
        }

        if (!isEqual(prevProps.filterModel, this.props.filterModel)) {
            this.grid.api.setFilterModel(this.props.filterModel);
        }

        if (!isEqual(prevProps.customerId, this.props.customerId)) {
            this.toggleCustomerColumn(this.props.customerId);
        }
    }

    toggleCustomerColumn = customerId => {
        this.grid.columnApi.getColumn("product_price").colDef.headerName = customerId
            ? "Customer Price"
            : "List Price";
        this.grid.api.refreshHeader();
        setTimeout(() => this.grid && this.grid.api.sizeColumnsToFit(), 300);
    };

    isInternalSkuActive = company => {
        this.grid.columnApi.setColumnVisible(
            "internal_sku",
            isFeatureActive(company, InternalSkuFeature)
        );
        setTimeout(() => this.grid && this.grid.api.sizeColumnsToFit(), 300);
    };

    reloadGridData = () => this.props.loadProductGridPage(this.props);

    applyNameFilter = (name = "") => {
        const nameFilter = this.grid.api.getFilterInstance("product");

        nameFilter.setModel({
            filterType: "text",
            type: "equals",
            filter: name,
        });

        this.grid.api.onFilterChanged();
    };

    handleDateRangeChange = (e, { value }) => {
        this.props.changeProductGridState({ dateRange: value });

        setTimeout(() => this.grid.api.refreshInfiniteCache(), 0);
    };

    handleCustomerChange = customer => {
        const customerId = get(customer, "id", null);
        this.props.changeProductGridState({ customerId, selectedCustomer: customer });

        if (this.props.onCustomerChange) {
            this.props.onCustomerChange(customer);
        }

        setTimeout(() => this.grid.api.refreshInfiniteCache(), 0);
    };

    handleExport = async ({ conditionalColumns, company }) => {
        this.setState({ isExporting: true, exportError: null });
        try {
            const products = await fetchAllProducts(this.props);
            const context = { customer_id: this.props.customerId };

            const columnKeys = Object.keys(allGridColumns).filter(field => {
                if (field === "id" || field === "archived") return false;
                if (!conditionalColumns.includes(field)) return true;
                if (field === "internal_sku") return isFeatureActive(company, InternalSkuFeature);
                if (field === "distributor")
                    return isFeatureActive(company, InventoryFeatureDetails);
                return false;
            });

            const columns = columnKeys.map(key => allGridColumns[key]);
            const header = columns.map(col => `"${col.headerName}"`).join(",");

            const rows = products.map(row => {
                return columns
                    .map(col => {
                        let value;
                        if (col.valueGetter) {
                            value = col.valueGetter({ data: row, context });
                        } else if (col.valueFormatter) {
                            value = col.valueFormatter({ data: row, context });
                        } else {
                            value = row[col.field];
                        }
                        // Escape quotes in values
                        const str = value != null ? String(value).replace(/"/g, '""') : "";
                        return `"${str}"`;
                    })
                    .join(",");
            });

            const csv = [header, ...rows].join("\n");
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `products_${Date.now()}.csv`;
            link.click();
            URL.revokeObjectURL(url);
            this.setState({ isExporting: false });
        } catch (err) {
            this.setState({ isExporting: false, exportError: "Export failed: " + err.message });
        }
    };

    toggleArchiveFilter = () => {
        const archivedFilter = this.grid.api.getFilterInstance("archived");
        const current = archivedFilter.getModel();

        archivedFilter.setModel({
            type: "equals",
            filter: current ? (current.filter === 1 ? 0 : 1) : 1,
        });

        this.grid.api.onFilterChanged();
    };

    render() {
        const { filterModel, customerId, company } = this.props;
        const { isExporting, exportError } = this.state;
        const hasFilterOtherThanArchived = !isEmpty(omit(filterModel, ["archived"]));
        const onlyArchived = filterModel.archived && filterModel.archived.filter === 1;
        const conditionalColumns = ["distributor", "internal_sku"];

        // Ensure product_price column has correct header name based on customer selection
        const columnDefs = this.props.columnDefs.map(col => {
            if (col.field === "product_price") {
                return {
                    ...col,
                    headerName: customerId ? "Customer Price" : "List Price",
                };
            }
            return col;
        });

        return (
            <>
                <div className="clear bm-10">
                    {exportError && (
                        <Message
                            negative
                            onDismiss={() => this.setState({ exportError: null })}
                            header="Export failed"
                            content={exportError}
                        />
                    )}
                    <GridPageSizePicker
                        button
                        compact
                        pointing="top"
                        pageSize={this.props.pageSize}
                        className="right floated basic"
                        text={`${this.props.pageSize}/page`}
                        onChange={this.props.changeProductGridState}
                    />

                    {hasFilterOtherThanArchived && (
                        <Button
                            size="mini"
                            icon="filter"
                            floated="right"
                            content="Clear Filters"
                            onClick={() =>
                                this.grid.api.setFilterModel(pick(filterModel, ["archived"]))
                            }
                            data-cy="products_clear_filters"
                        />
                    )}

                    <CustomerContainerPicker
                        dropdownProps={{
                            size: "mini",
                            fluid: false,
                            clearable: true,
                            placeholder: "Select Customer",
                        }}
                        selectedCustomerId={customerId}
                        onSelect={this.handleCustomerChange}
                    />

                    <Button
                        size="mini"
                        floated="right"
                        basic={!onlyArchived}
                        onClick={this.toggleArchiveFilter}
                        icon={onlyArchived ? "archive" : "checkmark"}
                        content={onlyArchived ? "Archived" : "Active"}
                    />

                    <Button
                        size="mini"
                        icon="download"
                        floated="right"
                        content={isExporting ? "Exporting..." : "Export"}
                        disabled={isExporting}
                        loading={isExporting}
                        onClick={() => this.handleExport({ conditionalColumns, company })}
                    />

                    <DatesRangeInput
                        size="mini"
                        className="fr"
                        clearable={true}
                        animation={null}
                        maxDate={new Date()}
                        value={this.props.dateRange}
                        placeholder="Invoiced Date Range"
                        dateFormat={DateFormat.toUpperCase()}
                        onChange={this.handleDateRangeChange}
                    />
                </div>
                <div className="ag-theme-balham bm-10 clear product-grid">
                    <AgGridReact
                        pagination
                        paginationPageSize={this.props.pageSize}
                        floatingFilter={true}
                        rowSelection="single"
                        domLayout="autoHeight"
                        rowModelType="infinite"
                        onGridReady={this.onGridReady}
                        columnDefs={columnDefs}
                        cacheBlockSize={5 * this.props.pageSize}
                        onPaginationChanged={this.changePage}
                        // TODO: For some reason, when the data is still being loaded, no rows overlay is shown
                        noRowsOverlayComponent={NoProductsOverlay}
                        loadingOverlayComponent={LoadingProductsOverlay}
                        defaultColDef={{
                            resizable: true,
                            sortable: true,
                            floatingFilter: true,
                            wrapHeaderText: true,
                            autoHeaderHeight: true,
                        }}
                        context={{ customer_id: this.props.customerId }}
                    ></AgGridReact>
                </div>
            </>
        );
    }
}

const mapStateToProps = ({ productGrid, auth }) => ({
    products: productGrid.products,
    pageSize: productGrid.pageSize,
    isLoading: productGrid.isLoading,
    sortModel: productGrid.sortModel,
    dateRange: productGrid.dateRange,
    customerId: productGrid.customerId,
    columnDefs: productGrid.columnDefs,
    filterModel: productGrid.filterModel,
    currentPage: productGrid.currentPage,
    selectedCustomer: productGrid.selectedCustomer,
    company: auth.user.company,
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            loadProductGridPage,
            changeProductGridState,
            loadProductForEdit,
            showProductEditorModal,
            changeProductGridPage: page => dispatch(changeProductGridPage(page)),
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps, null, {
    forwardRef: true,
})(DataGrid);
